import { ErrorObject } from '@thoughtspot/blink-context';
import { logger } from '@thoughtspot/client-state-util/esm/utils';
import {
    EventType,
    getInterceptTimeout,
    getInterceptUrls,
} from '@thoughtspot/embed-util';
import { dispatchNonMemoEvent } from '/@utils/event-bridge/event-bridge';

/**
 * Checks if the error is sent by a user.
 * This can happen in cases of api intercept and user sending custom errors.
 * @param errors - The errors to check
 * @returns True if the error is sent by a user, false otherwise
 */
export const isUserSentError = (errors: ErrorObject[]) => {
    return !!errors?.[0]?.errorObj?.isUserError;
};

/**
 * Returns the user sent error message and title.
 * @param errors - The errors to fetch the user sent error message and title from
 * @returns The user sent error message and title
 */
export const getUserSentErrorMessage = (errors: ErrorObject[]) => {
    return {
        description: errors?.[0]?.errorObj?.description,
        title: errors?.[0]?.errorObj?.title,
    };
};

/** Expected response from the api intercept */
type InterceptResponse = {
    data?: {
        execute?: boolean;
        response?: {
            body?: any;
            status?: number;
            headers?: any;
        };
    };
};

const INTERCEPT_TIMEOUT_ERROR = 'Intercept timed out';
const waitForIntercept = async (
    input: RequestInfo | URL,
    init?: RequestInit,
): Promise<[InterceptResponse | null, Error | null]> => {
    return new Promise(resolve => {
        try {
            dispatchNonMemoEvent(
                EventType.ApiIntercept,
                JSON.stringify({ input, init }),
            )
                .then(response =>
                    resolve([response as InterceptResponse, null]),
                )
                .catch(error => resolve([null, error]));

            const interceptTimeout = getInterceptTimeout();

            setTimeout(() => {
                const error = new Error(INTERCEPT_TIMEOUT_ERROR);
                resolve([null, error]);
            }, interceptTimeout);
        } catch (error) {
            resolve([null, error]);
        }
    });
};

/**
 * This will be used to intercept all URLs.
 */
const ALL_URL_CONSTANT = 'ALL';

const FALLBACK_ERROR_MESSAGE = 'Error in api intercept';
export const createFetchWithIntercept = (nativeFetch: typeof fetch) => async (
    input: RequestInfo | URL,
    init?: RequestInit,
): Promise<Response> => {
    const urlLink = input instanceof Request ? input.url : input.toString();
    const urlToFetch = new URL(urlLink, window.origin);

    const urlsToIntercept = getInterceptUrls() || [];

    const shouldInterceptUrl =
        urlsToIntercept.includes(urlToFetch.toString()) ||
        urlsToIntercept.includes(ALL_URL_CONSTANT);

    if (!shouldInterceptUrl) {
        return nativeFetch(input, init);
    }

    const [response, interceptError] = await waitForIntercept(input, init);

    if (interceptError) {
        logger.error(FALLBACK_ERROR_MESSAGE, interceptError);
        dispatchNonMemoEvent(EventType.Error, {
            error: interceptError.message || FALLBACK_ERROR_MESSAGE,
        });
        throw interceptError;
    }
    const shouldExecute = response.data?.execute === true;

    if (shouldExecute) {
        return nativeFetch(input, init);
    }

    const responseFromUser = response?.data?.response || {};

    const DEFAULT_HEADERS = {
        'Content-Type': 'application/json',
    };

    const fetchResponse = new Response(JSON.stringify(responseFromUser.body), {
        status: responseFromUser.status || 200,
        headers: {
            ...DEFAULT_HEADERS,
            ...responseFromUser.headers,
        },
    });

    return fetchResponse;
};
